import { ArrowLeftIcon } from "../../Icons";
import { Typo } from "../../Typo";

export type PerformanceReportBackLinkProps = {
  /** Deep-link back to the originating page (Referral Links / Marketing Banners). */
  href: string;
  /** Pre-resolved label — "Referral links" or "Marketing banners". The portal
   *  resolves this from the `from` param; the component renders nothing without it. */
  label: string;
  /** Runs before navigation (mirrors ViewPerformanceButton). */
  onClick?: () => void;
  /** In-app override — when supplied, prevents default anchor navigation and lets
   *  the host app drive its own router. Omitting it (backoffice) leaves a plain anchor. */
  onNavigate?: (href: string) => void;
};

/**
 * Page-level back link shown above the Performance Report heading when the
 * page was reached from a known origin (Referral Links / Marketing Banners).
 * Presentational only — no router import, so the same component mounts
 * under both the portal (TanStack Router) and backoffice (Next.js) hosts.
 */
export const BackLink = ({
  href,
  label,
  onClick,
  onNavigate,
}: PerformanceReportBackLinkProps) => {
  if (!href || !label) return null;

  return (
    <a
      href={href}
      className="flex w-fit items-center gap-2 text-text-link-default transition-colors hover:text-text-link-hover"
      onClick={(event) => {
        onClick?.();
        const isPlainPrimaryClick =
          event.button === 0 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.shiftKey &&
          !event.altKey;

        if (onNavigate && isPlainPrimaryClick) {
          event.preventDefault();
          onNavigate(href);
        }
      }}
    >
      <ArrowLeftIcon
        className="size-4 shrink-0"
        aria-hidden="true"
        focusable="false"
      />
      <Typo
        element="span"
        type="labelMD"
        smType="labelSM"
        textColor="text-inherit"
      >
        {label}
      </Typo>
    </a>
  );
};
